import { ConfigurationSourceDataFormVersion, ConfigurationSourceDataMap, Metadata, Trial } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import { useMemo } from "react";
import { ConfigurationType } from ".";



const excludedConfigurationSourceDataFormVersionCopyProperties: Partial<Record<keyof ConfigurationSourceDataFormVersion, boolean>> = {
    id: true,
    trialId: true,
    configurationSourceDataId: true,
    studyEventOid: true,
    caseReportFormOid: true,
    caseReportFormVersionOid: true,
    linked: true,
    enteredBy: true,
    enteredDate: true,
    modifiedBy: true,
    modifiedDate: true
}

export const copyConfigurationSourceDataFormVersionSettings = (
    source: ConfigurationSourceDataFormVersion,
    target: ConfigurationSourceDataFormVersion,

) => {
    for (let pName in source) {
        const propertyName = pName as keyof ConfigurationSourceDataFormVersion;

        if (!excludedConfigurationSourceDataFormVersionCopyProperties[propertyName]) {
            (target[propertyName] as any) = source[propertyName] as any;
        }
    }

    return target;
};

export const createFormVersionConfiguration = (
    metadata?: Metadata, 
    trialConfigurationFormVersions?: ConfigurationSourceDataMap['formVersions'], 
    configurationFormVersions?: ConfigurationSourceDataMap['formVersions'], 
    trial?: Trial, 
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    baseRoute?: string
) => {
    
    if (!metadata || !metadata.formVersions || !caseReportFormOid || !caseReportFormVersionOid) {
        return undefined;
    }

    const formVersionMetadata = metadata.formVersions?.[caseReportFormOid]?.[caseReportFormVersionOid];

    if (!formVersionMetadata) {
        return undefined;
    }

    const formVersionConfiguration = configurationFormVersions?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid];
    const eventFormVersionConfiguration = studyEventOid ? configurationFormVersions?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid] : undefined;

    const trialFormVersionConfiguration = trialConfigurationFormVersions?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid];
    const trialEventFormVersionConfiguration = studyEventOid ? trialConfigurationFormVersions?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid] : undefined;


    const dominantConfiguration = eventFormVersionConfiguration?.linked === false ? 
        eventFormVersionConfiguration : 
        trialEventFormVersionConfiguration?.linked === false ?
            trialEventFormVersionConfiguration :
            formVersionConfiguration?.linked === false ?
                formVersionConfiguration :
                trialFormVersionConfiguration

    const parentRoute = baseRoute ?
        baseRoute :
        `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}`;

    const configurationLink = studyEventOid ? 
        `${parentRoute}/events/${studyEventOid}/forms/${formVersionMetadata?.caseReportFormOid}/versions/${formVersionMetadata.caseReportFormVersionOid}` :
        `${parentRoute}/forms/${formVersionMetadata?.caseReportFormOid}/versions/${formVersionMetadata.caseReportFormVersionOid}`

    return {
        formVersionMetadata: formVersionMetadata,
        formVersionConfiguration: formVersionConfiguration,
        eventFormVersionConfiguration: eventFormVersionConfiguration,
        trialFormVersionConfiguration : trialFormVersionConfiguration,
        trialEventFormVersionConfiguration: trialEventFormVersionConfiguration,
        configuration: dominantConfiguration,

        linked: studyEventOid ?
            eventFormVersionConfiguration?.linked !== false :
            formVersionConfiguration?.linked !== false,

        type: eventFormVersionConfiguration?.linked === false ?
            ConfigurationType.Event :
            trialEventFormVersionConfiguration?.linked === false ?
                ConfigurationType.TrialEvent :
                formVersionConfiguration?.linked === false ?
                    ConfigurationType.Form :
                    ConfigurationType.Trial,

        icon: AppSettings.icons.formVersion,

        studyEventOid: studyEventOid,
        caseReportFormOid: formVersionMetadata.caseReportFormOid,
        caseReportFormVersionOid: formVersionMetadata.caseReportFormVersionOid,

        displayName: dominantConfiguration?.name ?? formVersionMetadata?.name,

        priority: dominantConfiguration?.priority,
            
        configurationLink: configurationLink,
        configurationItemsLink: `${configurationLink}/items`
    };
}

const useFormVersionConfiguration = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial, 
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    baseRoute?: string
) => {
    return  useMemo(() => {
        return createFormVersionConfiguration(
            metadata, 
            trialConfiguration?.formVersions, 
            configuration?.formVersions, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id ?? configurationSourceDataId, 
            studyEventOid,
            caseReportFormOid,
            caseReportFormVersionOid,
            baseRoute
        );
    }, [metadata, trialConfiguration?.formVersions, configuration?.formVersions, studyEventOid, caseReportFormOid, caseReportFormVersionOid, trial, configurationSourceDataId, baseRoute]);
};

export default useFormVersionConfiguration;